import {Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort, SortDirection} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {
  FabTableState,
  filterStateChange
} from '@fabric/components/table-filter/data-query';
import {catchError, EMPTY, map, Observable, of} from 'rxjs';
import {ErrorCodes} from '../../../constants/ErrorCodes';
import {UserFunctionCode} from '../../../constants/UserFunctionCode';
import {OtdsError} from '../../../models/OtdsError';
import {NotificationService} from '../../../services/notification.service';
import {OtdsErrorService} from '../../../services/otds-error.service';
import {SecurityService} from '../../../services/security.service';
import {
  GenericConfirmationDialogComponent
} from '../../../shared-components/generic-confirmation-dialog/generic-confirmation-dialog.component';
import {
  FabDateFilterOperator,
  RemDateFilterComponent
} from '../../../shared-components/table-filter/date-filter/date-filter.component';
import {RemNumberFilterComponent} from '../../../shared-components/table-filter/number-filter/number-filter.component';
import {
  FabStringFilterOperator,
  RemStringFilterComponent
} from '../../../shared-components/table-filter/string-filter/string-filter.component';
import {RowDataViewerComponent} from '../row-data-viewer/row-data-viewer.component';

@Component({
  selector: 'app-otds-error-processing',
  templateUrl: './otds-error-processing.component.html',
  styleUrls: ['./otds-error-processing.component.scss']
})
export class OtdsErrorProcessingComponent implements OnInit {

  isLoading: boolean = false;
  initialLoadingCompleted = false;

  isArchiveProcessing: boolean = false;
  isReprocessProcessing: boolean = false;
  isDownloadingExcel: boolean = false;


  get isProcessing(): boolean {
    return this.isArchiveProcessing || this.isReprocessProcessing || this.otdsErrors.some(e => e.isProcessing);
  }

  displayedColumns: string[] = ['nombreDeArchivo', 'lineNum', 'vin', 'nroRemito', 'origen', 'codDestino',
    'transporte', 'tpreError', 'error', 'fecProceso', 'reprocessButtons'];
  dataSource: MatTableDataSource<OtdsError> = new MatTableDataSource<OtdsError>();
  otdsErrors: OtdsError[] = [];
  operators: FabStringFilterOperator[] = ['contains'];
  public dateOperators: FabDateFilterOperator[] = ['eq', 'gte', 'lte'];
  state: FabTableState = {
    skip: 0,
    take: 5,
    filter: [],
    sort: []
  };
  selector: string;

  public get isNoRecordSelected(): boolean {
    return this.otdsErrors.every(((element) => !element.isChecked));
  }

  public get isRecordSelected(): boolean {
    return !this.isNoRecordSelected;
  }

  public get isCheckAll() {
    return this.dataSource.data.length > 0 && this.dataSource.data.every(e => e.isChecked);
  }

  showReprocessButton: boolean = false;
  showArchiveButton: boolean = false;

  @ViewChild(MatSort) matSort: MatSort;
  initialSortField = 'fecProceso';
  initialSortDirection: SortDirection = 'desc';

  pageSizes = [10, 15, 20, 25];
  totalElements: number = 0;

  @ViewChild(MatPaginator) matPaginator: MatPaginator;

  @ViewChild('dataTable', {read: ElementRef}) dataTable: ElementRef;

  @ViewChildren('tableFilter') tableFilters: QueryList<RemStringFilterComponent | RemNumberFilterComponent | RemDateFilterComponent>;

  constructor(private otdsErrorService: OtdsErrorService, private notificationService: NotificationService,
              private securityService: SecurityService,
              private elementRef: ElementRef,
              public dialog: MatDialog,) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.showReprocessButton = this.securityService.can(UserFunctionCode.OTDS_REPROCESS);
    this.showArchiveButton = this.securityService.can(UserFunctionCode.OTDS_REPROCESS);
    if (this.showReprocessButton) {
      this.displayedColumns.unshift('checkbox');
    }
    this.selector = this.elementRef.nativeElement.tagName.toLowerCase();
  }

  ngAfterViewInit(): void {
    this.fetchInitialData();
  }

  fetchInitialData() {
    this.isLoading = true;
    this.initialLoadingCompleted = false;
    this.matSort.active = this.initialSortField;
    this.matSort.direction = this.initialSortDirection;
    this.tableFilters.toArray().forEach((x) => x.clearFilter());
    this.matSort.sortChange.emit();
    this.requestPage().subscribe(() => {
      this.isLoading = false;
      this.initialLoadingCompleted = true;
      this.isArchiveProcessing = false;
      this.isReprocessProcessing = false;
      this.otdsErrors.forEach(e => {
        e.isArchiveProcessing = false
        e.isReprocessProcessing = false;
      });
    });
  }

  public filterChange(event: any) {
    this.uncheckAll();
    this.state.skip = 0;
    this.state.filter = filterStateChange(event, this.state.filter ?? []);
    this.updatePageState();
  }

  reprocessMultipleOtdsFiles() {
    const checked = this.otdsErrors.filter(element => element.isChecked);
    let confirmation: Observable<boolean> = of(true);
    this.isReprocessProcessing = true;
    if (checked.some(e => !e.canBeReprocessed)) {
      confirmation = this.confirmIneligibleRecordsSelected('reprocess');
    }
    confirmation.subscribe({
      next: (c) => {
        if (c) {
          const ids = checked.filter(e => e.canBeReprocessed).map(e => e.id);
          this.executeReprocess(ids);
        } else {
          this.isReprocessProcessing = false;
        }
      }
    });
  }

  reprocessSingleOtdsFile(element: OtdsError) {
    if (!element.canBeReprocessed) {
      this.notificationService.open(ErrorCodes.OTDS_ERROR_UNREPROCESSABLE, 'error');
      return;
    }
    element.isReprocessProcessing = true;
    this.executeReprocess([element.id]);
  }

  executeReprocess(ids: number[]) {
    this.otdsErrorService.reprocessOtdsErrors(ids).subscribe({
      next: (res) => {
        this.notificationService.open(ErrorCodes.OTDS_REPROCESS_SUCCESS.replace("{x}", String(res)), 'success');
        this.fetchInitialData();
      },
      error: (error) => {
        this.notificationService.open(ErrorCodes.OTDS_REPROCESS_FAILED, 'error');
        this.fetchInitialData();
      }
    });
  }

  clickCheckAll() {
    const allChecked = this.isCheckAll;
    this.dataSource.data.forEach(e => e.isChecked = !allChecked);
  }

  private uncheckAll() {
    this.otdsErrors.forEach(e => e.isChecked = false);
  }

  requestPage(): Observable<any> {
    return this.otdsErrorService.getErrorsPage(
      this.matPaginator.pageIndex,
      this.matPaginator.pageSize,
      this.matSort.active == "vin" ? ["vinLetras", "vinNumeros"] : [this.matSort.active],
      this.matSort.direction,
      this.state.filter ?? []
    ).pipe(
      map((response) => {
        this.otdsErrors = response.data;
        this.dataSource = new MatTableDataSource<OtdsError>(this.otdsErrors);
        this.totalElements = response.total;
        this.matPaginator.pageIndex = response.page;
        this.matPaginator.pageSize = response.pageSize;
        this.scrollTableToTop();
        return EMPTY;
      }), catchError((error) => {
        this.notificationService.open(this.notificationService.getErrorByCode(error.error.errorCode), 'error');
        this.otdsErrors = [];
        this.dataSource = new MatTableDataSource<OtdsError>([]);
        this.totalElements = 0;
        return EMPTY;
      })
    );
  }

  updatePageState() {
    if (this.initialLoadingCompleted) {
      this.isLoading = true;
      this.requestPage().subscribe(() => {
        this.isLoading = false;
      });
    }
  }

  onSortChange() {
    this.matPaginator.pageIndex = 0;
    this.updatePageState();
  }

  scrollTableToTop() {
    this.dataTable.nativeElement.scrollIntoView();
  }

  showRowDataEditingDialog(row: OtdsError) {
    this.dialog.open(RowDataViewerComponent, {data: {id: row.id}});
  }

  archiveSingleRecord(element: OtdsError) {
    /** Commented out because users requested to be able to archive records that can be reprocessed.
     * Users sometimes fix the error manually by creating a new remito instead of using reprocess feature.
     * When users already fixed the error manually, reprocess feature no longer works and the error record is stuck in this table.
     * Warning dialog from confirmIneligibleRecordsSelected() will remind user that some records can be reprocessed
     * User can double-check before deleting.
     **/
    // if (element.canBeReprocessed) {
    //   this.notificationService.open(ErrorCodes.OTDS_ERROR_UNARCHIVABLE, 'error');
    //   return;
    // }

    /**
     * If the record can be reprocessed, show a warning dialog to confirm the user's intention to archive the record.
     * If the user confirms, archive the record.
     * If the user cancels, do nothing.
     * If the record cannot be reprocessed, archive the record without showing a warning dialog.
     */
    if (element.canBeReprocessed) {
      this.confirmIneligibleRecordsSelected('archive').subscribe({
        next: (c) => {
          if (c) {
            this.archiveRecord(element);
          }
        }
      });
    } else {
      this.archiveRecord(element);
    }
  }

  private archiveRecord(element: OtdsError) {
    element.isArchiveProcessing = true;
    this.executeArchive([element.id]);
  }

  archiveMultipleRecords() {
    const checked = this.otdsErrors.filter(element => element.isChecked);
    let confirmation: Observable<boolean> = of(true);
    this.isArchiveProcessing = true;
    if (checked.some(e => e.canBeReprocessed)) {
      confirmation = this.confirmIneligibleRecordsSelected('archive');
    }
    confirmation.subscribe({
      next: (c) => {
        if (c) {
          const ids = checked.map(e => e.id);
          this.executeArchive(ids);
        } else {
          this.isArchiveProcessing = false;
        }
      }
    });
  }

  executeArchive(ids: number[]) {
    this.otdsErrorService.archiveOtdsErrors(ids).subscribe({
      next: (res) => {
        this.notificationService.open(ErrorCodes.OTDS_ARCHIVE_SUCCESS, 'success');
        this.fetchInitialData();
      },
      error: (error) => {
        this.notificationService.open(ErrorCodes.OTDS_ARCHIVE_FAILED, 'error');
        this.fetchInitialData();
      }
    });
  }

  confirmIneligibleRecordsSelected(action: 'archive' | 'reprocess'): Observable<boolean> {
    const warnTitle = {
      archive: 'Reprocessable Records Selected',
      reprocess: 'Ineligible Records Selected'
    }
    const warnMessage = {
      archive: 'One or more record(s) can be reprocessed. Do you still want to delete?',
      reprocess: `One or more of the selected records are ineligible for ${action} and will be deselected.`
    }
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      data: {
        titleText: warnTitle[action],
        message: warnMessage[action],
      }
    });

    return dialogRef.afterClosed()
  }

  getIsReprocessableColumnLabel(row: OtdsError) : string {
    return row.canBeReprocessed ? "autorenew" : "delete";
  }

  print() {

    this.isDownloadingExcel = true;
    this.otdsErrorService.downloadExcel().subscribe({
      next: (response) => {
        const newBlob = new Blob([response], {type: 'application/vnd.ms-excel'});
        const data = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = data;

        // @ts-ignore
        const fileName = 'OTDS_Errors_' + new Date().toLocaleString().replace('Z', '') + '.xlsx';
        link.download = fileName;
        link.click();
        this.isDownloadingExcel = false;
      },
      error: (error) => {
        this.notificationService.open(this.notificationService.getErrorByCode(error.errorCode), 'error')
        this.isDownloadingExcel = false;
      }
    })
  }



}
